import React from "react";
import { useAppDispatch, useAppSelector } from "../../../../../../redux";
import { getUspDetailsSagaAction } from "../../../../../../redux/saga/projects/saga-actions";
import { UspCategory } from "../../../../../../redux/slices/projects/Types";
import "./UspDetailsSection.scss";

type UspGroupProps = {
  label: string;
  values: string[];
};

const UspGroup: React.FC<UspGroupProps> = ({ label, values }) => {
  return (
    <div className="usp-details-section__group">
      <div className="usp-details-section__group-header">
        <span className="usp-details-section__label">{label}</span>
        <span className="usp-details-section__divider" aria-hidden="true" />
      </div>
      <div className="usp-details-section__tags">
        {values.map((value, index) => (
          <span key={`${label}-${index}`} className="usp-details-section__tag">
            {value}
          </span>
        ))}
      </div>
    </div>
  );
};

const mapUspCategoriesToGroups = (categories: UspCategory[]): UspGroupProps[] =>
  categories
    .map((category) => ({
      label: category.category_name,
      values: (category.features || [])
        .map((feature) => feature.feature_name)
        .filter(Boolean),
    }))
    .filter((group) => group.values.length > 0);

type UspDetailsSectionProps = {
  technologyId: number;
  integrationTypeId: number;
  integrationId: number;
  documentationId: number;
};

const UspDetailsSection: React.FC<UspDetailsSectionProps> = ({
  technologyId,
  integrationTypeId,
  integrationId,
  documentationId,
}) => {
  const dispatch = useAppDispatch();
  const { uspDetails } = useAppSelector((state) => state.projects);
  const { uspDetailsLoading } = useAppSelector((state) => state.projects.status);

  React.useEffect(() => {
    const isValidId = (id: number) => Number.isFinite(id) && id > 0;
    if (
      !isValidId(technologyId) ||
      !isValidId(integrationTypeId) ||
      !isValidId(integrationId) ||
      !isValidId(documentationId)
    ) {
      return;
    }
    dispatch(
      getUspDetailsSagaAction(technologyId, integrationTypeId, integrationId, documentationId)
    );
  }, [dispatch, technologyId, integrationTypeId, integrationId, documentationId]);

  const groups = React.useMemo(
    () => mapUspCategoriesToGroups(uspDetails || []),
    [uspDetails]
  );

  const renderBody = () => {
    if (uspDetailsLoading) {
      return (
        <p className="usp-details-section__empty">Loading USP details...</p>
      );
    }

    if (groups.length === 0) {
      return (
        <p className="usp-details-section__empty">No USP details available.</p>
      );
    }

    return groups.map((group) => (
      <UspGroup key={group.label} label={group.label} values={group.values} />
    ));
  };

  return (
    <div className="usp-details-section">
      <div className="usp-details-section__header">
        <h3 className="usp-details-section__title">Features Implemented</h3>
      </div>

      <div className="usp-details-section__body">{renderBody()}</div>
    </div>
  );
};

export default UspDetailsSection;
